import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage, availableLanguages } from '../contexts/LanguageContext';
import { privacyPolicy } from '../content/legal/privacyPolicy';
import { publicOffer } from '../content/legal/publicOffer';
import './LegalDocumentPage.css';

const DOCS = {
  privacy: privacyPolicy,
  offer: publicOffer,
};

const LANG_CODES = availableLanguages.map((l) => l.code);

function resolveLang(paramLang, contextLang) {
  if (paramLang && LANG_CODES.includes(paramLang)) {
    return paramLang;
  }
  if (contextLang && LANG_CODES.includes(contextLang)) {
    return contextLang;
  }
  return 'uz';
}

export default function LegalDocumentPage({ docType }) {
  const { lang: paramLang } = useParams();
  const navigate = useNavigate();
  const { language, switchLanguage, t, getCurrentLanguage } = useLanguage();
  const lang = resolveLang(paramLang, language);
  const docMap = DOCS[docType];
  const doc = docMap?.[lang] ?? docMap?.uz;

  useEffect(() => {
    if (paramLang && LANG_CODES.includes(paramLang) && paramLang !== language) {
      switchLanguage(paramLang);
    }
  }, [paramLang, language, switchLanguage]);

  useEffect(() => {
    document.title = `${doc?.title ?? 'ADDE'} — ADDE`;
    return () => {
      document.title = 'ADDE';
    };
  }, [doc?.title]);

  const basePath = docType === 'privacy' ? '/privacy' : '/offer';
  const otherDocPath = docType === 'privacy' ? '/offer' : '/privacy';
  const otherDocLabel =
    docType === 'privacy'
      ? { uz: 'Ommaviy oferta', ru: 'Публичная оферта', en: 'Public offer' }[lang]
      : { uz: 'Maxfiylik siyosati', ru: 'Политика конфиденциальности', en: 'Privacy policy' }[lang];

  const dateLabel = { uz: 'Kuchga kirish sanasi', ru: 'Дата вступления в силу', en: 'Effective date' }[lang];

  return (
    <div className="App legal-page">
      <Header />
      <main className="legal-main">
        <div className="container legal-container">
          <nav className="legal-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">{t('navHome') || 'Bosh sahifa'}</Link>
            <span className="legal-breadcrumb-sep">/</span>
            <span>{doc?.title}</span>
          </nav>

          <header className="legal-header">
            <h1 className="legal-title">{doc?.title}</h1>
            <p className="legal-meta">
              {dateLabel}: <strong>{doc?.effectiveDate}</strong>
            </p>
            <p className="legal-intro">{doc?.intro}</p>

            <div className="legal-toolbar">
              <div className="legal-lang-switch" role="group" aria-label="Language">
                {availableLanguages.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    className={`legal-lang-btn${lang === l.code ? ' legal-lang-btn--active' : ''}`}
                    onClick={() => navigate(`${basePath}/${l.code}`, { replace: true })}
                  >
                    {l.nativeName}
                  </button>
                ))}
              </div>
              <Link to={`${otherDocPath}/${lang}`} className="legal-related-link">
                {otherDocLabel} →
              </Link>
            </div>
          </header>

          <article className="legal-article">
            {doc?.sections?.map((section) => (
              <section key={section.title} className="legal-section">
                <h2 className="legal-section-title">{section.title}</h2>
                {section.body.map((paragraph, idx) => (
                  <p key={idx} className="legal-paragraph">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </article>

          <footer className="legal-footer-note">
            <p>
              {lang === 'ru'
                ? 'По вопросам: '
                : lang === 'en'
                  ? 'Questions: '
                  : 'Savollar uchun: '}
              <a href="mailto:info@adde.app">info@adde.app</a>
            </p>
            <p className="legal-footer-lang">
              {getCurrentLanguage()?.nativeName} ·{' '}
              <a href={`${basePath}/uz`}>O‘zbek</a>
              {' · '}
              <a href={`${basePath}/ru`}>Русский</a>
              {' · '}
              <a href={`${basePath}/en`}>English</a>
            </p>
          </footer>
        </div>
      </main>
      <Footer />
    </div>
  );
}
